import React, { createContext, useContext, type ReactNode } from 'react';
import { useThemeSwitcher } from '@gastrosoft/shared/theme';
import { useSetupGating } from '@gastrosoft/shared/contexts';
import { useOrganization } from '@/app/contexts/OrganizationContext';
import { clearSetupData } from '@/app/modules/setup/utils/setupStorage';

interface SetupContextType {
  needsSetup: boolean;
  checkingSetup: boolean;
  hasNetworkError: boolean;
  setNeedsSetup: (needs: boolean) => void;
  resetSetup: () => void;
}

const SetupContext = createContext<SetupContextType | undefined>(undefined);

export const useSetup = () => {
  const context = useContext(SetupContext);
  if (!context) {
    throw new Error('useSetup must be used within a SetupProvider');
  }
  return context;
};

interface SetupProviderProps {
  children: ReactNode;
}

export const SetupProvider: React.FC<SetupProviderProps> = ({ children }) => {
  const { currentBrand } = useThemeSwitcher();
  const {
    organizations,
    loading: organizationsLoading,
    organizationsQueryData,
    organizationsQueryError,
    tenantQueryError,
  } = useOrganization();

  const value = useSetupGating({
    enabled: currentBrand === 'orda-pos',
    organizations,
    organizationsLoading,
    organizationsQueryData,
    organizationsQueryError,
    tenantQueryError,
    resetSetupData: clearSetupData,
  });

  return <SetupContext.Provider value={value}>{children}</SetupContext.Provider>;
};
